import dayjs from 'dayjs';
import {
  Asset,
  AssetStatus,
  OnboardingQuestionType,
  QuestionValidationType,
} from '../generated/gql-types';
import type { DocumentChildQuestion } from './documentSlots.util';

export function parseAssetMetadata(
  metadata: Asset['metadata'],
): Record<string, unknown> {
  if (!metadata) {
    return {};
  }
  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return metadata as Record<string, unknown>;
}

export function getMetadataDateMs(
  metadata: Record<string, unknown>,
  keys: string[],
): number | null {
  for (const key of keys) {
    const raw = metadata[key];
    if (raw == null || raw === '') {
      continue;
    }
    const ms = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (!Number.isFinite(ms) || ms <= 0) {
      continue;
    }
    const d = dayjs(ms);
    if (d.isValid()) {
      return ms;
    }
  }
  return null;
}

export function formatMetadataDate(ms: number | null): string | null {
  if (ms == null) {
    return null;
  }
  return dayjs(ms).format('D MMM YYYY');
}

export function epochMsToDateInputValue(ms: number | null): string {
  if (ms == null) {
    return '';
  }
  return dayjs(ms).format('YYYY-MM-DD');
}

export type AssetDateDisplay = {
  issueDateLabel: string | null;
  expiryDateLabel: string | null;
  isExpired: boolean;
};

export function getAssetDateDisplay(asset: Asset): AssetDateDisplay {
  const meta = parseAssetMetadata(asset.metadata);
  const issueMs = getMetadataDateMs(meta, ['issueDate', 'issuedAt']);
  const expiryMs = getMetadataDateMs(meta, [
    'expiryDate',
    'expiresAt',
    'expirationDate',
  ]);

  const isExpired =
    asset.status === AssetStatus.Expired ||
    (expiryMs != null && dayjs(expiryMs).isBefore(dayjs(), 'day'));

  const issueFormatted = formatMetadataDate(issueMs);
  const expiryFormatted = formatMetadataDate(expiryMs);

  return {
    issueDateLabel: issueFormatted ? `Issued ${issueFormatted}` : null,
    expiryDateLabel: expiryFormatted
      ? isExpired
        ? `Expired ${expiryFormatted}`
        : `Expires ${expiryFormatted}`
      : null,
    isExpired: Boolean(expiryMs && isExpired),
  };
}

export type ChildQuestionValues = Record<string, string>;

function isDateChild(child: DocumentChildQuestion): boolean {
  return child.type === OnboardingQuestionType.Date;
}

/** DATE children with a future/today min constraint drive asset expiry (`expiresAt`). */
function isExpiryDateChild(child: DocumentChildQuestion): boolean {
  if (!isDateChild(child)) {
    return false;
  }
  return (child.validations ?? []).some(
    (validation) =>
      validation.type === QuestionValidationType.MinDate &&
      (validation.value === 'future' || validation.value === 'today'),
  );
}

function isIssueDateChild(child: DocumentChildQuestion): boolean {
  if (!isDateChild(child)) {
    return false;
  }
  return (child.validations ?? []).some(
    (validation) =>
      validation.type === QuestionValidationType.MaxDate &&
      validation.value === 'today',
  );
}

function dateInputToEpochMsString(dateInput: string): string {
  return String(dayjs(dateInput).startOf('day').valueOf());
}

function dateLookupKeys(child: DocumentChildQuestion): string[] {
  const keys = [child.key];
  // Legacy storage aliases used by older uploads
  if (isIssueDateChild(child)) {
    keys.push('issuedAt');
  }
  if (isExpiryDateChild(child)) {
    keys.push('expiresAt', 'expirationDate');
  }
  return keys;
}

/**
 * Build asset metadata from child-question form values.
 * Field encoding is driven by question `type` (DATE → epoch ms, others → string).
 * Expiry DATE children (MIN_DATE future/today) also write `expiresAt` for asset status.
 */
export function buildChildQuestionMetadata(
  values: ChildQuestionValues,
  childQuestions: DocumentChildQuestion[],
  options?: { includeEmpty?: boolean },
): Record<string, string> {
  const includeEmpty = options?.includeEmpty ?? false;
  const metadata: Record<string, string> = {};

  for (const child of childQuestions) {
    const raw = values[child.key]?.trim() ?? '';
    const writesExpiresAt = isExpiryDateChild(child);

    if (!raw) {
      if (includeEmpty) {
        metadata[child.key] = '';
        if (writesExpiresAt) {
          metadata.expiresAt = '';
        }
      }
      continue;
    }

    if (isDateChild(child)) {
      const ms = dateInputToEpochMsString(raw);
      metadata[child.key] = ms;
      if (writesExpiresAt) {
        metadata.expiresAt = ms;
      }
      continue;
    }

    metadata[child.key] = raw;
  }

  return metadata;
}

/** Initialise form values from an existing asset's metadata. */
export function childQuestionValuesFromAsset(
  asset: Asset | null | undefined,
  childQuestions: DocumentChildQuestion[],
): ChildQuestionValues {
  const values: ChildQuestionValues = {};
  const meta = parseAssetMetadata(asset?.metadata);

  for (const child of childQuestions) {
    if (isDateChild(child)) {
      values[child.key] = epochMsToDateInputValue(
        getMetadataDateMs(meta, dateLookupKeys(child)),
      );
      continue;
    }

    const raw = meta[child.key];
    values[child.key] = raw == null ? '' : String(raw);
  }

  return values;
}

export function validateRequiredChildQuestions(
  values: ChildQuestionValues,
  childQuestions: DocumentChildQuestion[],
): string | null {
  for (const child of childQuestions) {
    if (child.required && !values[child.key]?.trim()) {
      return `${child.title} is required`;
    }
  }
  return null;
}

export type ChildAnswerSummaryItem = {
  key: string;
  label: string;
  isWarning?: boolean;
  isExpired?: boolean;
};

function formatOptionLabel(
  child: DocumentChildQuestion,
  value: string,
): string {
  const option = child.options?.find((item) => item.value === value);
  return option?.label ?? value;
}

function formatChildAnswerForDisplay(
  child: DocumentChildQuestion,
  meta: Record<string, unknown>,
  asset: Asset,
): ChildAnswerSummaryItem | null {
  switch (child.type) {
    case OnboardingQuestionType.Date: {
      const ms = getMetadataDateMs(meta, dateLookupKeys(child));
      if (ms == null) {
        if (child.required) {
          return {
            key: child.key,
            label: `Missing ${child.title.toLowerCase()}`,
            isWarning: true,
          };
        }
        return null;
      }

      const formatted = formatMetadataDate(ms);
      if (!formatted) {
        return null;
      }

      if (isIssueDateChild(child)) {
        return { key: child.key, label: `Issued ${formatted}` };
      }

      if (isExpiryDateChild(child)) {
        const isExpired =
          asset.status === AssetStatus.Expired ||
          dayjs(ms).isBefore(dayjs(), 'day');
        return {
          key: child.key,
          label: isExpired ? `Expired ${formatted}` : `Expires ${formatted}`,
          isExpired,
        };
      }

      return { key: child.key, label: `${child.title}: ${formatted}` };
    }
    case OnboardingQuestionType.Dropdown:
    case OnboardingQuestionType.SingleSelect:
    case OnboardingQuestionType.Radio: {
      const raw = meta[child.key];
      const text = raw == null ? '' : String(raw).trim();
      if (!text) {
        if (child.required) {
          return {
            key: child.key,
            label: `Missing ${child.title.toLowerCase()}`,
            isWarning: true,
          };
        }
        return null;
      }
      return {
        key: child.key,
        label: `${child.title}: ${formatOptionLabel(child, text)}`,
      };
    }
    default: {
      const raw = meta[child.key];
      const text = raw == null ? '' : String(raw).trim();
      if (!text) {
        if (child.required) {
          return {
            key: child.key,
            label: `Missing ${child.title.toLowerCase()}`,
            isWarning: true,
          };
        }
        return null;
      }
      return { key: child.key, label: `${child.title}: ${text}` };
    }
  }
}

export function getAssetChildAnswersSummary(
  asset: Asset,
  childQuestions: DocumentChildQuestion[],
): ChildAnswerSummaryItem[] {
  if (childQuestions.length === 0) {
    const { issueDateLabel, expiryDateLabel, isExpired } =
      getAssetDateDisplay(asset);
    const items: ChildAnswerSummaryItem[] = [];
    if (issueDateLabel) {
      items.push({ key: 'issueDate', label: issueDateLabel });
    }
    if (expiryDateLabel) {
      items.push({
        key: 'expiryDate',
        label: expiryDateLabel,
        isExpired,
      });
    }
    return items;
  }

  const meta = parseAssetMetadata(asset.metadata);
  const items: ChildAnswerSummaryItem[] = [];
  for (const child of childQuestions) {
    const item = formatChildAnswerForDisplay(child, meta, asset);
    if (item) {
      items.push(item);
    }
  }
  return items;
}

/** Resolve min/max attributes for a DATE child from onboarding validations. */
export function getDateInputBounds(child: DocumentChildQuestion): {
  min?: string;
  max?: string;
} {
  const today = dayjs().format('YYYY-MM-DD');
  let min: string | undefined;
  let max: string | undefined;

  for (const validation of child.validations ?? []) {
    switch (validation.type) {
      case QuestionValidationType.MaxDate: {
        if (validation.value === 'today') {
          max = today;
        } else if (/^\d{4}-\d{2}-\d{2}$/.test(validation.value)) {
          max = validation.value;
        }
        break;
      }
      case QuestionValidationType.MinDate: {
        if (validation.value === 'future' || validation.value === 'today') {
          min = today;
        } else if (/^\d{4}-\d{2}-\d{2}$/.test(validation.value)) {
          min = validation.value;
        }
        break;
      }
      default:
        break;
    }
  }

  return { min, max };
}

/** @deprecated Prefer buildChildQuestionMetadata */
export function buildUploadMetadata(
  dates: Partial<Record<'issueDate' | 'expiryDate', string>>,
): Record<string, string> {
  const children = (
    [
      {
        key: 'issueDate',
        title: 'Issue date',
        type: OnboardingQuestionType.Date,
        required: false,
        validations: [
          {
            type: QuestionValidationType.MaxDate,
            value: 'today',
            errorMessage: '',
          },
        ],
      },
      {
        key: 'expiryDate',
        title: 'Expiry date',
        type: OnboardingQuestionType.Date,
        required: false,
        validations: [
          {
            type: QuestionValidationType.MinDate,
            value: 'future',
            errorMessage: '',
          },
        ],
      },
    ] as DocumentChildQuestion[]
  ).filter((child) => Boolean(dates[child.key as 'issueDate' | 'expiryDate']));

  return buildChildQuestionMetadata(
    {
      issueDate: dates.issueDate ?? '',
      expiryDate: dates.expiryDate ?? '',
    },
    children,
  );
}

/** @deprecated Prefer validateRequiredChildQuestions */
export function validateRequiredDocumentDates(
  dates: Partial<Record<'issueDate' | 'expiryDate', string>>,
  requirements: Partial<
    Record<'issueDate' | 'expiryDate', { required: boolean; title: string }>
  >,
): string | null {
  const children: DocumentChildQuestion[] = [];
  for (const key of ['issueDate', 'expiryDate'] as const) {
    const requirement = requirements[key];
    if (!requirement) {
      continue;
    }
    children.push({
      key,
      title: requirement.title,
      type: OnboardingQuestionType.Date,
      required: requirement.required,
    });
  }
  return validateRequiredChildQuestions(
    {
      issueDate: dates.issueDate ?? '',
      expiryDate: dates.expiryDate ?? '',
    },
    children,
  );
}
